import FormWriter from './form_writer';

/**
 * Applies an item's actions to the closest dashboard form and submits it.
 *
 * Entry points take the element rather than a controller so a host app can
 * drive dashboard interactions from its own extension controllers. Those run in
 * a separate Stimulus application and load in whatever order the host's layout
 * dictates, so they can neither resolve a controller instance registered in this
 * bundle nor reliably subclass a class from it at module-evaluation time.
 */
export default class Actions {
  /**
   * Resolves a `$token` placeholder against the triggering interaction's
   * context, so a component that produces more than one value (e.g. a period's
   * range plus its granularity) declares each one in its action rather than
   * replacing this. An unknown token is left as written, so a literal value that
   * happens to start with `$` still reaches the form.
   */
  static resolve(template, context) {
    if (typeof template !== 'string' || !template.startsWith('$')) return template;

    const key = template.slice(1);

    return key in context ? context[key] : template;
  }

  /**
   * A null namespace writes the un-prefixed key, so an action without one still
   * resolves to exactly one write into the global namespace.
   */
  static namespacesFor(scoped) {
    return scoped.namespace?.length ? scoped.namespace : [null];
  }

  /**
   * `submit: false` writes the inputs without submitting, for a control that has
   * to seed the shared form on load but must leave the decision to submit (and
   * how many submits a page load costs) to its caller.
   */
  static apply(element, actions, context, { resolver = Actions.resolve, submit = true } = {}) {
    const form = FormWriter.for(element);
    if (!form) return null;
    if (!actions || actions.length === 0) return null;

    new Actions(form, resolver).#applyAll(actions, context);
    if (submit) form.submit();

    return form;
  }

  constructor(form, resolver) {
    this.form = form;
    this.resolver = resolver;
  }

  #applyAll(actions, context) {
    actions.forEach((action) => {
      const namespace = action.namespace || [];
      const ctx = { ...context, namespace };

      if (action.write?.visuals) this.#setVisuals(action.write.visuals, ctx);

      if (action.write?.sorts) this.#setSorts(action.write.sorts, ctx);

      if (action.write?.paginations) this.#setPaginations(action.write.paginations, ctx);

      if (action.write?.filters) {
        if (ctx.remove) {
          this.#removeFilter(action.write.filters, ctx);
        } else {
          this.#setFilter(action.write.filters, ctx);
        }
      }
    });
  }

  #setVisuals(visualWrites, context) {
    Object.entries(visualWrites).forEach(([key, template]) => {
      const value = this.resolver(template, context);

      Actions.namespacesFor(context).forEach((namespace) => {
        this.form.setVisual(namespace, key, value);
      });
    });
  }

  #setSorts(sortWrites, context) {
    Object.entries(sortWrites).forEach(([member, entry]) => {
      const resolvedMember = this.resolver(member, context);
      const direction = this.resolver(entry.direction, context);

      Actions.namespacesFor(context).forEach((namespace) => {
        this.form.setSort(namespace, resolvedMember, direction);
      });
    });
  }

  #setPaginations(paginationWrites, context) {
    Object.entries(paginationWrites).forEach(([key, entry]) => {
      const page = this.resolver(entry.page, context);
      const limit = this.resolver(entry.limit, context);

      Actions.namespacesFor(context).forEach((namespace) => {
        this.form.setPagination(namespace, key, page, limit);
      });
    });
  }

  #setFilter(filterWrites, context) {
    Object.entries(filterWrites).forEach(([member, filterDef]) => {
      const resolvedMember = this.resolver(member, context);
      const value = this.resolver(filterDef.value, context);
      const operator = filterDef.operator || 'eq';

      Actions.namespacesFor(context).forEach((namespace) => {
        this.form.setFilter(namespace, resolvedMember, operator, value);
      });
    });
  }

  #removeFilter(filterWrites, context) {
    Object.keys(filterWrites).forEach((member) => {
      const resolvedMember = this.resolver(member, context);

      Actions.namespacesFor(context).forEach((namespace) => {
        this.form.removeFilter(namespace, resolvedMember);
      });
    });
  }
}
